import { useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";

import { useGetAllCategories } from "../../../../api/jeep-rally/categories/categories.hooks";
import {
  useGetActiveRally,
  useGetRallyById,
  useGetRallyStages,
  useGetRallies,
} from "../../../../api/jeep-rally/rally/rally.hooks";
import {
  useDeleteRankingsSnapshotMutation,
  useGetRankingsByEvent,
} from "../../../../api/jeep-rally/rankings/rankings.hooks";
import { useGetAllTypes } from "../../../../api/jeep-rally/types/types.hooks";
import {
  isJeepRallyAdmin,
  normalizeCategories,
} from "../Categories/utils/category.utils";
import {
  getCategorySelectValue,
  getRallyCategories,
  normalizeRallies,
  normalizeRally,
} from "../Rallies/utils/rally.utils";
import { normalizeTypes } from "../Types/utils/type.utils";
import {
  filterCategoriesByType,
  getRankingApiErrorMessage,
  getSnapshotStageLabel,
  normalizeRankings,
  normalizeStages,
  resolveRallyTypes,
} from "./utils/ranking.utils";
import RankingsFilters from "./List/_components/RankingsFilters";
import RankingsTable from "./List/_components/RankingsTable";

const RankingListPage = () => {
  const history = useHistory();
  const location = useLocation();
  const canManage = isJeepRallyAdmin();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [selectedRallyId, setSelectedRallyId] = useState(
    () => query.get("eventId") || ""
  );
  const [selectedType, setSelectedType] = useState(
    () => query.get("type") || ""
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => query.get("category") || ""
  );
  const [selectedStage, setSelectedStage] = useState(
    () => query.get("after_stage") || ""
  );
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  useEffect(() => {
    const eventId = query.get("eventId") || "";
    const type = query.get("type") || "";
    const category = query.get("category") || "";
    const afterStage = query.get("after_stage") || "";
    if (eventId) setSelectedRallyId(eventId);
    if (type) setSelectedType(type);
    if (category) setSelectedCategory(category);
    if (afterStage !== "") setSelectedStage(afterStage);
  }, [query]);

  const {
    data: activeRallyData,
    isLoading: isActiveRallyLoading,
  } = useGetActiveRally();

  const activeRally = useMemo(
    () => normalizeRally(activeRallyData),
    [activeRallyData]
  );
  const activeEventId = activeRally?._id || activeRally?.id || "";

  const {
    data: ralliesData,
    isLoading: isRalliesLoading,
    isError: isRalliesError,
    error: ralliesError,
  } = useGetRallies();

  const rallies = useMemo(() => {
    const list = normalizeRallies(ralliesData);
    if (!activeEventId) return list;

    return list.map((rally) => {
      const id = rally._id || rally.id;
      return id === activeEventId ? { ...rally, isActive: true } : rally;
    });
  }, [ralliesData, activeEventId]);

  useEffect(() => {
    if (selectedRallyId) return;
    if (activeEventId) {
      setSelectedRallyId(activeEventId);
      return;
    }
    if (!isRalliesLoading && rallies.length) {
      const firstId = rallies[0]?._id || rallies[0]?.id || "";
      if (firstId) setSelectedRallyId(firstId);
    }
  }, [activeEventId, isRalliesLoading, rallies, selectedRallyId]);

  const {
    data: rallyData,
    isLoading: isRallyLoading,
  } = useGetRallyById(selectedRallyId);

  const { data: typesData, isLoading: isTypesLoading } = useGetAllTypes({
    status: "active",
  });
  const allTypes = useMemo(() => normalizeTypes(typesData), [typesData]);

  const { data: allCategoriesData, isLoading: isAllCategoriesLoading } =
    useGetAllCategories();

  const allCategories = useMemo(
    () => normalizeCategories(allCategoriesData),
    [allCategoriesData]
  );

  const rally = useMemo(() => normalizeRally(rallyData), [rallyData]);

  const types = useMemo(
    () => resolveRallyTypes(rally, allTypes),
    [rally, allTypes]
  );

  const rallyCategories = useMemo(() => {
    const rallyCats = getRallyCategories(rally);
    if (!rallyCats.length) return allCategories;

    return rallyCats
      .map((category) => {
        if (category && typeof category === "object" && category.key) {
          return category;
        }
        const key =
          typeof category === "string"
            ? category
            : getCategorySelectValue(category);
        const match = allCategories.find(
          (item) =>
            item.key === key || item._id === key || item.id === key
        );
        return match || { key, title: key };
      })
      .filter((category) => category?.key || category?._id || category?.id);
  }, [rally, allCategories]);

  const categories = useMemo(
    () => filterCategoriesByType(rallyCategories, selectedType),
    [rallyCategories, selectedType]
  );

  const {
    data: stagesData,
    isLoading: isStagesLoading,
  } = useGetRallyStages(selectedRallyId, {
    enabled: Boolean(selectedRallyId),
  });

  const stages = useMemo(() => normalizeStages(stagesData), [stagesData]);

  const rankingParams = useMemo(() => {
    const params = {
      type: selectedType,
      categoryId: selectedCategory,
    };
    if (selectedStage !== "") {
      params.after_stage = Number(selectedStage);
    }
    return params;
  }, [selectedType, selectedCategory, selectedStage]);

  const {
    data: rankingsData,
    isLoading: isRankingsLoading,
    isFetching: isRankingsFetching,
    isError: isRankingsError,
    error: rankingsError,
  } = useGetRankingsByEvent(selectedRallyId, rankingParams, {
    enabled:
      Boolean(selectedRallyId) &&
      Boolean(selectedType) &&
      Boolean(selectedCategory),
  });

  const rankings = useMemo(
    () => normalizeRankings(rankingsData),
    [rankingsData]
  );

  const {
    mutate: deleteSnapshot,
    isLoading: isDeleting,
    isPending: isDeletePending,
  } = useDeleteRankingsSnapshotMutation();
  const deleting = isDeleting || isDeletePending;

  const snapshotStage = getSnapshotStageLabel(rankings, selectedStage);

  const buildCreateSearch = ({
    eventId = selectedRallyId,
    type = selectedType,
    category = selectedCategory,
    afterStage = selectedStage,
  } = {}) => {
    const search = new URLSearchParams();
    if (eventId) search.set("eventId", eventId);
    if (type) search.set("type", type);
    if (category) search.set("category", category);
    if (afterStage !== "") search.set("after_stage", afterStage);
    return search.toString();
  };

  const handleRallyChange = (rallyId) => {
    setSelectedRallyId(rallyId);
    setSelectedType("");
    setSelectedCategory("");
    setSelectedStage("");
    setFeedback({ type: "", message: "" });
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setSelectedCategory("");
    setFeedback({ type: "", message: "" });
  };

  const handleAddRankings = () => {
    if (!selectedRallyId || !canManage) return;
    history.push(
      `/jeep-rally/ranking/create?${buildCreateSearch()}`
    );
  };

  const handleEditSnapshot = (entry) => {
    if (!canManage) return;
    const type =
      (typeof entry?.type === "string" && entry.type) ||
      entry?.type_doc?.key ||
      selectedType;
    const category =
      (typeof entry?.category === "string" && entry.category) ||
      entry?.category_doc?.key ||
      selectedCategory;
    const afterStage =
      entry?.after_stage === 0 || entry?.after_stage
        ? String(entry.after_stage)
        : selectedStage;

    history.push(
      `/jeep-rally/ranking/create?${buildCreateSearch({
        type,
        category,
        afterStage,
      })}`
    );
  };

  const handleDeleteSnapshot = () => {
    if (
      !canManage ||
      !selectedRallyId ||
      !selectedType ||
      !selectedCategory ||
      selectedStage === ""
    ) {
      setFeedback({
        type: "warning",
        message:
          "Select type, category, and a specific after stage to delete that snapshot.",
      });
      return;
    }

    const confirmed = window.confirm(
      `Delete rankings snapshot for type "${selectedType}", category "${selectedCategory}", after stage ${selectedStage}?`
    );
    if (!confirmed) return;

    setFeedback({ type: "", message: "" });
    deleteSnapshot(
      {
        eventId: selectedRallyId,
        params: {
          type: selectedType,
          categoryId: selectedCategory,
          after_stage: Number(selectedStage),
        },
      },
      {
        onSuccess: () => {
          setFeedback({
            type: "success",
            message: "Rankings snapshot deleted.",
          });
        },
        onError: (error) => {
          setFeedback({
            type: "danger",
            message: getRankingApiErrorMessage(
              error,
              "Failed to delete rankings snapshot."
            ),
          });
        },
      }
    );
  };

  if (isRalliesError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load rallies.{" "}
            {getRankingApiErrorMessage(ralliesError, "Please try again.")}
          </div>
        </div>
      </div>
    );
  }

  const filtersReady =
    Boolean(selectedRallyId) &&
    Boolean(selectedType) &&
    Boolean(selectedCategory);
  const showRankingsLoading =
    filtersReady && (isRankingsLoading || isRankingsFetching);

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">
            Rankings
            {filtersReady && rankings.length
              ? ` (${rankings.length})`
              : ""}
          </h4>
        </div>

        <div className="card-body">
          <RankingsFilters
            rallies={rallies}
            isRalliesLoading={isRalliesLoading || isActiveRallyLoading}
            selectedRallyId={selectedRallyId}
            onRallyChange={handleRallyChange}
            activeRallyId={activeEventId}
            types={types}
            isTypesLoading={
              Boolean(selectedRallyId) && (isRallyLoading || isTypesLoading)
            }
            selectedType={selectedType}
            onTypeChange={handleTypeChange}
            categories={categories}
            isCategoriesLoading={
              Boolean(selectedRallyId) &&
              (isRallyLoading || isAllCategoriesLoading)
            }
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            stages={stages}
            isStagesLoading={Boolean(selectedRallyId) && isStagesLoading}
            selectedStage={selectedStage}
            onStageChange={setSelectedStage}
            canManage={canManage}
            onAddRankings={handleAddRankings}
            onDeleteSnapshot={handleDeleteSnapshot}
            addDisabled={!selectedRallyId}
            deleteDisabled={
              !selectedRallyId ||
              !selectedType ||
              !selectedCategory ||
              selectedStage === ""
            }
            deleting={deleting}
          />

          {!canManage && (
            <Alert variant="warning" className="mb-3">
              Admin access is required to add or edit rankings.
            </Alert>
          )}

          {feedback.message ? (
            <Alert
              variant={feedback.type || "info"}
              className="mb-3"
              dismissible
              onClose={() => setFeedback({ type: "", message: "" })}
            >
              {feedback.message}
            </Alert>
          ) : null}

          {!selectedRallyId ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">Select a rally to view rankings.</p>
            </div>
          ) : !selectedType ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">Select a type to continue.</p>
            </div>
          ) : !selectedCategory ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">
                Select a category to load rankings for this rally and type.
              </p>
            </div>
          ) : showRankingsLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : isRankingsError ? (
            <div className="text-danger py-4">
              Failed to load rankings.{" "}
              {getRankingApiErrorMessage(
                rankingsError,
                "Please try again."
              )}
            </div>
          ) : rankings.length === 0 ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">
                No rankings found for this rally, type, and category
                {selectedStage !== "" ? `, after stage ${selectedStage}` : ""}.
              </p>
            </div>
          ) : (
            <>
              <h5 className="mb-3">
                {snapshotStage === 0 || snapshotStage
                  ? `Rankings after stage ${snapshotStage}`
                  : "Latest rankings snapshot"}
              </h5>
              <RankingsTable
                rankings={rankings}
                canManage={canManage}
                onEditSnapshot={handleEditSnapshot}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RankingListPage;
